import { Tooltip } from "@mui/material";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { routingConstants } from "@/Constants/routingConstants";
import {
  deSelectAllRoles,
  fetchClientRoles,
  setClientRolesLoading,
  toggleSelectedAll,
  updatePagination,
  updateSelectedClientRoles,
  updateSorting,
  updateToggleSelected,
} from "@/reducers/client_user/roleAccess";
import { TOOLTIPTEXT } from "@/utilities/CommonText";
import { sortByText } from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { showErrorToast } from "@/utilities/utils";

import Actions from "./Actions";
import SearchFilter from "./SearchFilter";
import DataTablePaginationNoUserLoader from "../../../../Skeletons/DataTablePaginationNoUserLoader";
import TableLoader from "../../../TableLoader";
import TableNoContent from "../../../TableNoContent";
import { SelectedCountText } from "../../candidates/Listing/common/SelectedCountText";

const RoleList = () => {
  const {
    isLoading,
    roles,
    selectedRoles,
    totalCount,
    pagination,
    isSelectedAll,
    toggleSelected,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientRoles);
  const dispatch = useDispatch();
  const dataTableRef = useRef();

  useEffect(() => {
    if (selectedRoles.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedRoles]);

  // lazy loading for 16 records
  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchClientRoles({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  // check clicked on row is is_admin or not
  const handleRowClickAlert = (row) => {
    if (row.is_admin) {
      showErrorToast(
        "Action not allowed : You can't edit or delete Admin role",
        {
          id: "admin-selected",
        },
      );
    } else if (row.is_logged_user_role) {
      showErrorToast(
        "Action not allowed : You can't edit or delete your own role ",
        {
          id: "own-role-selected",
        },
      );
    }
  };

  const columns = [
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "role_name"),
      selector: (row) => (
        <Tooltip
          title={
            row.is_admin
              ? TOOLTIPTEXT.checkadminRole
              : row.is_logged_user_role
                ? TOOLTIPTEXT.checkOwnRole
                : ""
          }
          placement="top"
          arrow
        >
          <div className="flex flex-col">
            <div className="flex flex-col" data-tag="allowRowEvents">
              <p className="line-clamp-1 text-[14px] font-semibold capitalize leading-[normal] text-black">
                {row.role_name}
              </p>
            </div>
          </div>
        </Tooltip>
      ),
      width: "25%",
    },
    // role_description
    {
      name: "Description",
      sortable: true,
      sortField: "role_description",
      selector: (row) => (
        <div className="flex flex-col">
          <span className="w-[550px] truncate text-[14px] font-semibold leading-[normal] text-black">
            {row.role_description || "-"}
          </span>
        </div>
      ),
      width: "40%",
    },
    // {
    //   name: "Permissions",
    //   sortable: false,
    //   selector: (row) => (
    //     <div className="flex flex-col" data-tag="allowRowEvents">
    //       <span className="text-[14px] font-semibold leading-[normal] text-black">
    //         {row.permission_count || 0}
    //       </span>
    //     </div>
    //   ),
    //   width: "15%",
    // },
    {
      name: "status",
      sortable: false,
      selector: (row) => (
        <div className="flex select-none flex-col" data-tag="allowRowEvents">
          {row.status == "1" ? (
            <span
              className={`rounded-lg border border-green bg-white px-5 py-1 text-[14px] !text-green`}
            >
              Active
            </span>
          ) : (
            <Tooltip title={TOOLTIPTEXT.inactiverole} placement="top" arrow>
              <span className="cursor-not-allowed rounded-lg border border-red bg-white px-5 py-1 text-[14px] !text-red">
                Inactive
              </span>
            </Tooltip>
          )}
        </div>
      ),
      width: "",
    },
  ];

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(deSelectAllRoles());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setClientRolesLoading(true));
    dispatch(fetchClientRoles());
  };

  return (
    <div className="RoleList_Wrapper mb-[52px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="text-[20px] font-medium leading-[normal] text-black">
              Roles
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedRoles.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedClientRoles(val));
              }}
              currentDataLength={roles.length}
            />
          </div>
          <div className="flex gap-[20px]">
            <div className="tour-client-add-role flex gap-4">
              <Link to={routingConstants.clientAddRole}>
                <button className="transition-linear h-[40px] rounded-lg border border-primary bg-lightblue px-4 font-semibold leading-[normal] text-primary hover:bg-primary hover:text-white">
                  Add New Role
                </button>
              </Link>
            </div>
            <SearchFilter />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={roles}
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            noDataComponent={<TableNoContent />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedIDS = state.selectedRows.map((item) => {
                return item.role_id;
              });
              dispatch(updateSelectedClientRoles(selectedIDS));
            }}
            clearSelectedRows={toggleSelected}
            className="_DataTable"
            onRowClicked={handleRowClickAlert}
            selectableRowDisabled={(row) =>
              row.is_admin || row.is_logged_user_role
            }
            conditionalRowStyles={[
              {
                when: (row) => row.is_admin || row.is_logged_user_role,
                style: {
                  cursor: "not-allowed",
                },
              },
            ]}
            customStyles={{
              rows: {
                style: {
                  '& .rdt_TableCell input[type="checkbox"]:disabled ': {
                    border: "2px solid lightGrey !important", // Applies the grey border for admin's or own row checkbox
                  },
                },
              },
            }}
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationNoUserLoader />
        ) : null}
      </div>
    </div>
  );
};

export default RoleList;
